import { ChangeEvent, useEffect, useState } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useMutation } from 'react-query';
import { PiWarning } from 'react-icons/pi';

import { StyledCancelButton, StyledConfirmButton, StyledModal } from './styles';
import InvoiceHttpService from '../../../services/http/invoice-http';
import handleResponseError from '../../../utils/handleResponseError';
import ReturnsHttpService from '../../../services/http/return-http';

interface InvoiceReturnModalProps {
    show: boolean;
    invoiceId?: number;
    invoiceNf?: string;
    onHide: () => void;
}

export default function InvoiceReturnModal({
    show,
    invoiceId,
    invoiceNf,
    onHide,
}: InvoiceReturnModalProps) {
    const [returnReason, setReturnReason] = useState<string | undefined>(
        undefined,
    );
    const [loading, setLoading] = useState<boolean>(false);

    const confirmMutation = useMutation({
        mutationFn: async () => {
            if (!invoiceId) return;

            await ReturnsHttpService.saveReturn(invoiceId.toString(), {
                observation: returnReason,
            });
        },
        onError: (error: any) => {
            handleResponseError(
                error,
                'Ocorreu um erro ao solicitar a pré-devolução',
            );
        },
        onSuccess: () => {
            toast.success('Sucesso ao solicitar a pré-devolução!');
        },
        onSettled: () => {
            onHide();
        },
    });

    const handleChangeMessage = (event: ChangeEvent<HTMLInputElement>) => {
        setReturnReason(event.target.value);
    };

    const validatePermission = async () => {
        if (invoiceNf || !invoiceId) return;

        try {
            setLoading(true);
            const { data } = await InvoiceHttpService.validateIfHasNfM8(
                invoiceId,
            );
            if (!data) {
                toast.error(
                    'Não é possivel realizar a solicitação pois a nota fiscal ainda não foi emitida.',
                );
                onHide();
            }
        } catch (error) {
            handleResponseError(
                error,
                'Erro ao validar se a nota fiscal foi emitida',
            );
            onHide();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setReturnReason(undefined);

        if (invoiceId && show) {
            validatePermission();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceId, show]);

    return (
        <StyledModal show={show} onHide={onHide} centered>
            <Modal.Body className="text-center">
                <PiWarning />
                <Modal.Title>Solicitar devolução</Modal.Title>

                {loading ? (
                    <div aria-busy="true" aria-live="polite" className="my-3">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Form.Group controlId="returnReason">
                        <Form.Control
                            className="textarea"
                            as="textarea"
                            name="feedback"
                            value={returnReason}
                            onChange={handleChangeMessage}
                            rows={5}
                            placeholder="Insira o motivo da devolução..."
                            aria-label="Motivo da devolução"
                        />
                    </Form.Group>
                )}

                <StyledCancelButton
                    onClick={onHide}
                    disabled={confirmMutation.isLoading}
                >
                    Cancelar
                </StyledCancelButton>

                <StyledConfirmButton
                    onClick={confirmMutation.mutateAsync}
                    disabled={confirmMutation.isLoading || !returnReason}
                >
                    {confirmMutation.isLoading ? (
                        <Spinner animation="border" />
                    ) : (
                        'Confirmar'
                    )}
                </StyledConfirmButton>
            </Modal.Body>
        </StyledModal>
    );
}
